import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { NoDataChart } from './NoDataChart';
import { HEBREW_MONTH_NAMES_SHORT, HEBREW_MONTH_NAMES } from '../../utils/dateUtils';

const RevenueChart = ({ onFetchData }) => {
  const [startMonth, setStartMonth] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Set default dates to last 6 months
  React.useEffect(() => {
    const today = new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(today.getMonth() - 5);

    setEndMonth(today.toISOString().slice(0, 7));
    setStartMonth(sixMonthsAgo.toISOString().slice(0, 7));
  }, [])

  const handleFetchRevenue = async () => {
    if (!startMonth || !endMonth) {
      setError('נא לבחור טווח תאריכים');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const revenueData = await onFetchData(startMonth, endMonth);

      // Transform data for the chart
      const chartData = revenueData.map(item => ({
        month: item.month, // Keep full YYYY-MM format
        revenue: item.revenue,
        unpaid: item.unpaid,
        tooltip: {
          fullMonth: item.month,
          paidCount: item.paidCount,
          unpaidCount: item.unpaidCount,
          total: item.revenue + item.unpaid
        }
      }));

      setData(chartData);
      setDataLoaded(true);
    } catch (err) {
      setError(err.message || 'שגיאה בטעינת נתוני הכנסות');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (value) => {
    return `₪${value.toLocaleString('he-IL')}`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    const data = payload[0].payload.tooltip;

    return (
      <div className="bg-white/95 backdrop-blur-sm p-3 rounded-lg shadow-xl border border-gray-200">
        <p className="font-semibold text-gray-800 mb-2">{data.fullMonth}</p>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between gap-4">
            <span className="text-blue-600">הכנסות ({data.paidCount}):</span>
            <span className="font-medium">{formatPrice(payload[0].value)}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-red-600">טרם שולם ({data.unpaidCount}):</span>
            <span className="font-medium">{formatPrice(payload[1]?.value || 0)}</span>
          </div>
          <div className="pt-1 border-t border-gray-200">
            <div className="flex justify-between gap-4">
              <span className="font-semibold">סה"כ:</span>
              <span className="font-bold">{formatPrice(data.total)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
      <h2 className="text-xl font-bold text-gray-800 mb-6 text-right">דוח הכנסות חודשי</h2>

      {/* Date Range Selector */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
              מחודש
            </label>
            <input
              type="month"
              value={startMonth}
              onChange={(e) => setStartMonth(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-right"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
              עד חודש
            </label>
            <input
              type="month"
              value={endMonth}
              onChange={(e) => setEndMonth(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-right"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={handleFetchRevenue}
              disabled={loading}
              className="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:from-purple-600 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              {loading ? 'טוען...' : 'הצג הכנסות'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-right">
            {error}
          </div>
        )}
      </div>

      {/* Chart */}
      {data.length > 0 && (
        <div className="mt-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 rounded-xl p-4 text-center">
              <p className="text-sm text-blue-600 mb-1">סה"כ הכנסות</p>
              <p className="text-2xl font-bold text-blue-700">
                {formatPrice(data.reduce((sum, d) => sum + d.revenue, 0))}
              </p>
            </div>
            <div className="bg-red-50 rounded-xl p-4 text-center">
              <p className="text-sm text-red-600 mb-1">סה"כ טרם שולם</p>
              <p className="text-2xl font-bold text-red-700">
                {formatPrice(data.reduce((sum, d) => sum + d.unpaid, 0))}
              </p>
            </div>
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <p className="text-sm text-green-600 mb-1">סה"כ כולל</p>
              <p className="text-2xl font-bold text-green-700">
                {formatPrice(data.reduce((sum, d) => sum + d.revenue + d.unpaid, 0))}
              </p>
            </div>
          </div>

          {/* Bar Chart */}
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => {
                    const [year, month] = value.split('-');
                    const mIndex = parseInt(month, 10) - 1;
                    return `${HEBREW_MONTH_NAMES_SHORT[mIndex]} ${year.slice(2)}`;
                  }}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => `₪${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  formatter={(value) => value === 'revenue' ? 'הכנסות' : 'טרם שולם'}
                  wrapperStyle={{ paddingTop: '20px' }}
                />
                <Bar
                  dataKey="revenue"
                  stackId="a"
                  fill="#3B82F6"
                  radius={[0, 0, 0, 0]}
                  name="revenue"
                />
                <Bar
                  dataKey="unpaid"
                  stackId="a"
                  fill="#EF4444"
                  radius={[8, 8, 0, 0]}
                  name="unpaid"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Empty State - Initial */}
      {dataLoaded && !loading && data.length === 0 && !error && (
        <NoDataChart
          primaryMessage='אין מידע להציג'
          secondaryMessage='בצע חיפוש כדי להציג נתונים בטווח הנבחר'
          dataType='revenue' />
      )}

      {/* Empty State - No data found */}
      {!dataLoaded && !loading && data.length === 0 && !error && (
        <NoDataChart
          primaryMessage='אין מידע להציג'
          secondaryMessage='לא נמצאו נתונים בטווח הנבחר'
          dataType='revenue' />
      )}
    </div>
  );
};

export default RevenueChart;